import React, { useState, useRef, useEffect } from 'react';
import { Upload, Sparkles, Loader2, AlertCircle, Globe, Zap, Shield, Cpu, CheckCircle2, Terminal, Scan } from 'lucide-react';
// Import library Google AI
import { GoogleGenerativeAI } from "@google/generative-ai";

// --- MASUKKAN API KEY GEMINI ANDA DI SINI ---
const API_KEY = "YOUR_API"; // Ganti dengan API Key Anda yang sebenarnya

export default function AIImageAnalyzer() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState('');
  const [analysisType, setAnalysisType] = useState('general');
  const [language, setLanguage] = useState('id');
  const [useRealAPI, setUseRealAPI] = useState(false);

  const messagesEndRef = useRef(null);

  const translations = {
    en: {
      title: 'Neural Vision',
      subtitle: 'Advanced Decentralized Image Analysis Protocol',
      uploadText: 'Drop image or click to upload',
      analyzeBtn: 'Initialize Analysis',
      analyzing: 'Processing...',
      results: 'Analysis Data',
      demoMode: 'Simulation Mode',
      realAPI: 'Live Network',
      waitingText: 'System Standby... Waiting for visual input data...',
      steps: ['Initializing Neural Net...', 'Scanning Pixels...', 'Feature Extraction...', 'Synthesizing Output...'],
      fileTooLarge: 'File too large (Max 5MB)',
      selectImage: 'Please select an image first.',
      errorAnalyzing: 'Unable to analyze image. Please try again.'
    },
    id: {
      title: 'Neural Vision',
      subtitle: 'Protokol Analisis Gambar Terdesentralisasi',
      uploadText: 'Tarik gambar atau klik untuk unggah',
      analyzeBtn: 'Mulai Analisis',
      analyzing: 'Memproses...',
      results: 'Data Analisis',
      demoMode: 'Mode Simulasi',
      realAPI: 'Jaringan Langsung',
      waitingText: 'Sistem Siaga... Menunggu input visual...',
      steps: ['Menginisialisasi Neural Net...', 'Memindai Piksel...', 'Ekstraksi Fitur...', 'Menyusun Laporan...'],
      fileTooLarge: 'Ukuran file terlalu besar (Maks 5MB)',
      selectImage: 'Silakan pilih gambar terlebih dahulu.',
      errorAnalyzing: 'Tidak dapat menganalisis gambar. Silakan coba lagi.'
    }
  };

  const t = translations[language] || translations.en;

  // --- FUNGSI UTAMA ---

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t.fileTooLarge);
        return;
      }

      setSelectedImage(file);
      setError('');
      setAnalysis('');
      setLoading(false);

      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
    // Reset input agar bisa upload ulang gambar yang sama
    e.target.value = null;
  };

  // Konversi file ke format yang diterima Google Gemini (Base64)
  const fileToGenerativePart = async (file) => {
    const base64EncodedDataPromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
    };
  };

  const analyzeImage = async () => {
    if (!selectedImage) {
      setError(t.selectImage);
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis('');

    // Animasi Loading Bertahap
    const steps = t.steps;
    for (let i = 0; i < steps.length; i++) {
      setLoadingStep(steps[i]);
      await new Promise(r => setTimeout(r, 600)); // Delay kosmetik
    }

    if (!useRealAPI) {
      // --- MODE DEMO (SIMULASI) ---
      const demoResponse = language === 'id'
        ? `[MODE SIMULASI]\n\nSistem mendeteksi gambar: "${selectedImage.name}".\n\nAnalisis Simulasi:\n• Objek utama teridentifikasi dalam frame.\n• Pencahayaan dan komposisi telah dipindai.\n• Pola visual cocok dengan database neural.\n\n(Catatan: Ini adalah respons otomatis. Aktifkan "Jaringan Langsung" untuk analisis AI yang sebenarnya.)`
        : `[SIMULATION MODE]\n\nSystem detected image: "${selectedImage.name}".\n\nSimulation Analysis:\n• Primary objects identified in frame.\n• Lighting and composition scanned.\n• Visual patterns match neural database.\n\n(Note: This is an automated response. Enable "Live Network" for actual AI analysis.)`;

      setAnalysis(demoResponse);
      setLoading(false);

    } else {
      // --- MODE REAL API (GOOGLE GEMINI) ---
      try {
        // Validasi sederhana untuk memastikan API Key bukan string kosong atau default
        // Pastikan bukan kosong
        if (!API_KEY) {
          throw new Error("API Key belum dipasang. Edit file src/App.js.");
        }

        const genAI = new GoogleGenerativeAI(API_KEY);
        const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

        const prompts = {
          general: `Analisis gambar ini secara umum dalam bahasa ${language === 'id' ? 'Indonesia' : 'Inggris'}. Jelaskan detail visualnya.`,
          technical: `Lakukan analisis teknis (komposisi, warna, kualitas) gambar ini dalam bahasa ${language === 'id' ? 'Indonesia' : 'Inggris'}.`,
          creative: `Buatlah caption Instagram atau cerita pendek kreatif dari gambar ini dalam bahasa ${language === 'id' ? 'Indonesia' : 'Inggris'}.`,
          objects: `Deteksi semua objek dalam gambar ini. Berikan output format list bullet points. Bahasa: ${language === 'id' ? 'Indonesia' : 'Inggris'}.`
        };

        const imagePart = await fileToGenerativePart(selectedImage);
        const result = await model.generateContent([prompts[analysisType], imagePart]);
        const response = await result.response;
        const text = response.text();

        setAnalysis(text);
      } catch (err) {
        console.error("Error:", err);
        setError(`Gagal: ${err.message || "Koneksi Error"}`);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (analysis && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [analysis]);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-300 font-sans selection:bg-cyan-500/30 relative overflow-hidden">

      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-cyan-900/20 rounded-full blur-[120px] pointer-events-none animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none animate-pulse" style={{ animationDelay: '2s' }} />

      <div className="max-w-6xl mx-auto p-6 relative z-10">

        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl backdrop-blur-xl shadow-[0_0_15px_rgba(6,182,212,0.2)]">
              <Cpu className="w-8 h-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight">{t.title}</h1>
              <p className="text-cyan-500/60 text-xs md:text-sm font-medium tracking-widest uppercase">{t.subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <button
              onClick={() => setUseRealAPI(!useRealAPI)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${useRealAPI
                  ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                  : 'bg-purple-500/10 border-purple-500 text-purple-400'
                }`}
            >
              {useRealAPI ? <Zap className="w-4 h-4" /> : <Terminal className="w-4 h-4" />}
              {useRealAPI ? t.realAPI : t.demoMode}
            </button>

            <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-1 flex">
              <button onClick={() => setLanguage('en')} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${language === 'en' ? 'bg-slate-700 text-white' : 'text-slate-500'}`}>EN</button>
              <button onClick={() => setLanguage('id')} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${language === 'id' ? 'bg-slate-700 text-white' : 'text-slate-500'}`}>ID</button>
            </div>
          </div>
        </header>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-12 gap-8">

          {/* Left Column: Controls & Upload */}
          <div className="lg:col-span-5 space-y-6">

            <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-2xl p-6 shadow-xl">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 block flex items-center gap-2">
                <Shield className="w-3 h-3" /> Configuration Protocol
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['general', 'technical', 'creative', 'objects'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setAnalysisType(type)}
                    className={`p-3 rounded-xl border text-left transition-all relative overflow-hidden group ${analysisType === type
                        ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-300'
                        : 'bg-slate-800/50 border-transparent text-slate-400 hover:border-slate-600'
                      }`}
                  >
                    <span className="block text-xs font-medium capitalize relative z-10">{type}</span>
                    {analysisType === type && <div className="absolute inset-0 bg-cyan-400/5 animate-pulse"></div>}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-2xl p-6 shadow-xl">
              <label className="block w-full cursor-pointer group relative">
                <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-300 overflow-hidden relative ${selectedImage ? 'border-cyan-500/50 bg-cyan-900/5' : 'border-slate-700 hover:border-cyan-500/30 hover:bg-slate-800/50'
                  }`}>
                  {selectedImage ? (
                    <div className="flex flex-col items-center animate-in zoom-in duration-300">
                      <CheckCircle2 className="w-12 h-12 text-cyan-400 mb-3" />
                      <p className="text-white font-medium truncate max-w-[200px]">{selectedImage.name}</p>
                      <p className="text-xs text-cyan-500 mt-1">File Loaded Successfully</p>
                    </div>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                        <Upload className="w-8 h-8 text-slate-400 group-hover:text-cyan-400 transition-colors" />
                      </div>
                      <p className="text-slate-300 font-medium">{t.uploadText}</p>
                    </>
                  )}
                  <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                </div>
              </label>

              <button
                onClick={analyzeImage}
                disabled={!selectedImage || loading}
                className="w-full mt-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white py-4 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-cyan-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {loading ? t.analyzing : t.analyzeBtn}
                </span>
                <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-white/20 skew-x-[20deg] group-hover:left-[200%] transition-all duration-700"></div>
              </button>
            </div>
          </div>

          {/* Right Column: Visualization & Results */}
          <div className="lg:col-span-7 space-y-6">
            <div className="relative bg-black/40 rounded-2xl overflow-hidden border border-slate-800 min-h-[300px] flex items-center justify-center group shadow-2xl">
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Target" className="max-h-[400px] w-full object-contain" />
                  {loading && (
                    <div className="absolute inset-0 z-20 pointer-events-none">
                      <div className="absolute top-0 left-0 w-full h-1 bg-cyan-400 shadow-[0_0_20px_rgba(34,211,238,1)] animate-[scan_2s_linear_infinite]"></div>
                      <div className="absolute inset-0 bg-cyan-500/5"></div>
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur border border-cyan-500/50 text-cyan-400 text-xs font-mono px-3 py-1 rounded shadow-lg">
                        {loadingStep}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-10 opacity-30">
                  <Scan className="w-20 h-20 mx-auto mb-4 text-slate-500" />
                  <p className="text-sm uppercase tracking-widest text-slate-500">No Signal Input</p>
                </div>
              )}

              <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-xl"></div>
              <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-cyan-500/30 rounded-tr-xl"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-cyan-500/30 rounded-bl-xl"></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-cyan-500/30 rounded-br-xl"></div>
            </div>

            <div className="bg-[#0f172a] rounded-2xl border border-slate-800 overflow-hidden shadow-2xl flex flex-col h-[350px]">
              <div className="bg-slate-900/90 px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                <span className="ml-2 text-[10px] font-mono text-slate-500 tracking-wider">OUTPUT_CONSOLE_V2.0</span>
                <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-600">
                  <Globe className="w-3 h-3" />
                  {useRealAPI ? 'ONLINE' : 'OFFLINE'}
                </div>
              </div>

              <div className="p-6 font-mono text-sm leading-relaxed relative overflow-y-auto custom-scrollbar flex-1">
                {error ? (
                  <div className="text-red-400 flex items-start gap-3 bg-red-950/10 p-4 rounded-lg border border-red-900/30">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold mb-1">SYSTEM ERROR</p>
                      <p className="opacity-80">{error}</p>
                    </div>
                  </div>
                ) : analysis ? (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="flex items-center gap-2 text-cyan-400 mb-4 pb-2 border-b border-cyan-500/10">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="font-bold tracking-wider">{`ANALYSIS COMPLETE: [${analysisType.toUpperCase()}]`}</span>
                    </div>
                    <div className="text-slate-300 whitespace-pre-wrap leading-7">
                      {analysis}
                    </div>
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-700 gap-3">
                    <Terminal className="w-12 h-12 opacity-20" />
                    <p className="italic text-center">{t.waitingText}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #0f172a; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
      `}</style>
    </div>
  );
}